class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.hasUserInteracted = false;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.hasShownExtraMessageThisSession = false;

    this.setupEventListeners();
    // Only run header logic if modal exists
    if (document.querySelector('.search-modal')) {
      this.initHeaderIconSearchListener();
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  initHeaderIconSearchListener() {
    const headerIcons = document.querySelectorAll('.header__icon--search');

    headerIcons.forEach((icon) => {
      icon.addEventListener('click', () => {
        const modal = document.querySelector('.search-modal');
        const input = document.querySelector('.search-input');

        if (modal && input) {
          const isOpening = !modal.classList.contains('open');
          modal.classList.toggle('open');

          if (isOpening) {
            // Reset every search input so Athos opens on its empty state
            // (recent searches), not the previous query.
            document.querySelectorAll('.search-input').forEach((searchInput) => {
              searchInput.value = '';
              searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            });
          }

          input.focus();
        } else {
          console.warn('Search modal or input not found on this page');
        }
      });
    });

    // Add close functionality for overlay click and escape key
    this.initSearchModalCloseListeners();
  }

  initSearchModalCloseListeners() {
    const modal = document.querySelector('.search-modal');
    if (!modal) return;

    // Close modal when clicking outside the search content
    modal.addEventListener('click', (event) => {
      const searchContent = modal.querySelector('predictive-search');

      // If click is outside the search content area, close the modal
      if (searchContent && !searchContent.contains(event.target)) {
        this.closeSearchModal();
      }
    });

    // Close modal when pressing escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('open')) {
        this.closeSearchModal();
        event.preventDefault(); // Prevent default escape key behavior
      }
    });

    // Also add click listener to the overlay element specifically
    const overlay = document.querySelector('.search-modal .modal-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.closeSearchModal();
      });
    }

    // Add functionality to the existing close button
    const closeButton = document.querySelector('.search-modal__close-button');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeSearchModal();
      });
      // Make the close button visible
      closeButton.classList.remove('hidden');
    }
  }

  closeSearchModal() {
    const modal = document.querySelector('.search-modal');
    if (modal && modal.classList.contains('open')) {
      modal.classList.remove('open');

      // Clear the search input when closing
      const input = document.querySelector('.search-input');
      if (input) {
        input.value = '';
        input.blur();
      }

      // Close any open predictive search results
      this.close(true);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm, 'predictive-search-products');
    this.getSearchResults(this.searchTerm, 'predictive-search-collections');
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    const message = this.querySelector('[data-extra-message]');
    const hasResults = this.getAttribute('results') === 'true';

    if (!currentSearchTerm.length && !hasResults && !this.hasUserInteracted) {
      if (message) message.hidden = false;
    } else {
      if (message) message.hidden = true;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.hasUserInteracted = true; // mark user as having changed something
      this.onChange();
    } else if (hasResults) {
      this.open();
    } else if (currentSearchTerm.length) {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        this.hasUserInteracted = false; // reset interaction flag
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm, sectionId) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=${sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${sectionId}`).innerHTML;
        const containerAttribute = sectionId;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, containerAttribute);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    // this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, containerAttribute) {
    const container = this.predictiveSearchResults.querySelector(`[data-${containerAttribute}]`);
    container.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Hide the extra message (e.g., "Popular Searches") once results are displayed
    const extraMessage = this.querySelector('[data-extra-message]');
    if (extraMessage) extraMessage.hidden = true;

    this.open();
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;

    const message = this.querySelector('[data-extra-message]');
    if (message) message.hidden = true;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
